import * as React from 'react';
import { storiesOf } from '@storybook/react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import {
  RadioButtonGroup,
  RadioButtonGroupProps,
  RadioButton,
  RadioButtonGroupLayout,
  RadioButtonTheme,
} from '..';
import { classes } from './RadioButtonGroup.visual.st.css';
import { EditorShell, VisualAsync } from '../../test/visual/VisualAsync';
import { snap, story, visualize } from 'storybook-snapper';
import { radioButtonGroupDriverFactory } from './RadioButtonGroup.driver';

interface RadioButtonGroupVisualProps extends RadioButtonGroupProps {
  /* Render <hr> after the component to indicate where it ends. */
  showEndIndicator?: boolean;
}

class RadioButtonGroupVisual extends React.Component<RadioButtonGroupVisualProps> {
  render() {
    const { showEndIndicator, ...props } = this.props;
    return (
      <EditorShell>
        <VisualTestContainer>
          <RadioButtonGroup {...props} />
          {showEndIndicator && <hr />}
        </VisualTestContainer>
      </EditorShell>
    );
  }
}

const radioButtonEl = <RadioButton key={1} label="meow" value="meow" />;
const radioButtonElAnother = <RadioButton key={2} label="meow" value="meow" />;
const radioButtonEl2 = <RadioButton key={3} label="meow" value="meow" />;
const radioButtonDisabled = (
  <RadioButton key={4} label="meow" value="meow" disabled />
);

interface Tests {
  describe: string;
  its: {
    it: string;
    props: Partial<RadioButtonGroupVisualProps>;
    style?: Record<string, string>;
  }[];
}

const tests: Tests[] = [
  {
    describe: 'basic',
    its: [
      {
        it: 'default',
        props: {
          children: [radioButtonEl],
        },
      },
      {
        it: 'with label',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
        },
      },
      {
        it: 'with label',
        props: {
          withSpacing: true,
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
        },
      },
      {
        it: 'with fluid',
        props: {
          fluid: true,
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
        },
      },
      {
        it: 'with horizontal layout',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          layout: RadioButtonGroupLayout.Horizontal,
        },
      },
      {
        it: 'with horizontal layout with error text',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          layout: RadioButtonGroupLayout.Horizontal,
          errorText: "I'm an error",
          error: true,
        },
      },
      {
        it: 'with disabled',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          disabled: true,
        },
      },
      {
        it: 'with disabled children',
        props: {
          children: [radioButtonDisabled, radioButtonEl],
          label: "No worries. I'm a label.",
          disabled: false,
        },
      },
      {
        it: 'with error',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          error: true,
        },
      },
      {
        it: 'with error text',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          error: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'styled with error text',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          error: true,
          errorText: "I'm error text",
          className: classes.root,
          showEndIndicator: true,
        },
      },
      {
        it: 'with box theme',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
          theme: RadioButtonTheme.Box,
        },
      },
      {
        it: 'with box theme and multiple children',
        props: {
          children: [
            <RadioButton key={1} value="Unchecked" label="option1" />,
            <RadioButton key={2} value="Checked" checked label="option2" />,
            <RadioButton key={3} value="Unchecked" label="option1" />,
          ],
          theme: RadioButtonTheme.Box,
          value: 'Checked',
        },
      },
      {
        it: 'withSpacing',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          withSpacing: true,
        },
      },
      {
        it: 'withSpacing - box theme',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          withSpacing: true,
          theme: RadioButtonTheme.Box,
        },
      },
      {
        it: 'new error message without a message',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'new error message with a message',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'styled new error message with a message',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
          className: classes.root,
          showEndIndicator: true,
        },
      },
      {
        it: 'new error message / withSpacing',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
          withSpacing: true,
        },
      },
      {
        it: 'new error message / horizontal layout',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          layout: RadioButtonGroupLayout.Horizontal,
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message without a message - theme box',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          theme: RadioButtonTheme.Box,
        },
      },
      {
        it: 'new error message with a message  - theme box',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
          theme: RadioButtonTheme.Box,
        },
      },
      {
        it: 'new error message / withSpacing / theme box',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
          theme: RadioButtonTheme.Box,
          withSpacing: true,
        },
      },
      {
        it: 'new error message / horizontal layout / theme box',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          layout: RadioButtonGroupLayout.Horizontal,
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
          theme: RadioButtonTheme.Box,
        },
      },
      {
        it: 'with increased horizontal spacing',
        props: {
          children: [radioButtonEl, radioButtonElAnother, radioButtonEl2],
          label: "No worries. I'm a label.",
          layout: RadioButtonGroupLayout.Horizontal,
          className: classes.itemSpacing,
        },
      },
      {
        it: 'with wst theming',
        props: {
          children: [radioButtonEl],
          label: "No worries. I'm a label.",
        },
        style: {
          '--wst-paragraph-2-color-rgb': '198,0,0',
          '--wst-paragraph-2-font-family': 'Courier New, serif',
          '--wst-paragraph-2-font-size': '22px',
          '--wst-paragraph-2-font-weight': 'bold',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        },
      },
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props, style }) => {
    storiesOf(`RadioButtonGroup/${describe}`, module).add(it, () => (
      <div style={style}>
        <RadioButtonGroupVisual
          name="group"
          onChange={(e) => console.log(e)}
          {...props}
        />
      </div>
    ));
  });
});

visualize('RadioButtonGroup', () => {
  const themes = Object.values(RadioButtonTheme);
  const layouts = Object.values(RadioButtonGroupLayout);
  const fluids = [true, false];
  const props = themes.flatMap((theme) =>
    layouts.flatMap((layout) =>
      fluids.flatMap((fluid) => ({
        name: 'whatever',
        onChange: () => {},
        theme,
        layout,
        fluid,
      })),
    ),
  );

  const its = props.map((props) => ({
    it: `${props.theme} ${props.layout} ${props.fluid ? 'fluid' : ''}`,
    props,
  }));

  story('focus / with the new div', () => {
    its.forEach(({ it, props }) =>
      snap(it, (done) => (
        <VisualAsync
          driverFactory={radioButtonGroupDriverFactory}
          onDone={done}
          action={(driver) => driver.clickItemInput(1)}
        >
          <RadioButtonGroup {...props}>
            <RadioButton label="short label" value="short" />
            <RadioButton label="somewhat medium label" value="medium" />
            <RadioButton
              label="very very very very very long-ish label"
              value="long"
            />
          </RadioButtonGroup>
        </VisualAsync>
      )),
    );
  });
});
